import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Baby, Loader2, Heart, Moon, Droplet, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { api, relTime } from './api';
import { MaternalBackLink, MaternalDisclaimer, MaternalLoader } from './_ui';

/**
 * Maternal · Postpartum tracker
 *
 * Calm postpartum log: bleeding, mood, feeding, pain, sleep hours, notes.
 * Backend: POST /api/maternal/postpartum and GET /api/maternal/postpartum/recent.
 * Form is only enabled if the user is in postpartum/recovery stage; otherwise
 * we render a gentle "this section unlocks once you mark birth" message.
 *
 * Warnings are framed as "When to call your clinician" — never alarming.
 */

const BLEEDING = [
  { v: 'none',     label: 'None' },
  { v: 'light',    label: 'Light' },
  { v: 'moderate', label: 'Moderate' },
  { v: 'heavy',    label: 'Heavy' },
];
const MOOD = [
  { v: 'happy',    label: 'Happy',    emoji: '😊' },
  { v: 'okay',     label: 'Okay',     emoji: '🙂' },
  { v: 'low',      label: 'Low',      emoji: '😔' },
  { v: 'very_low', label: 'Very low', emoji: '🥺' },
];
const FEEDING = [
  { v: 'breastfeeding', label: 'Breastfeeding' },
  { v: 'mixed',         label: 'Mixed' },
  { v: 'formula',       label: 'Formula' },
  { v: 'none',          label: 'Not yet' },
];
const PAIN = [
  { v: 'none',     label: 'None' },
  { v: 'mild',     label: 'Mild' },
  { v: 'moderate', label: 'Moderate' },
  { v: 'strong',   label: 'Strong' },
];

const Pill = ({ value, current, onClick, testid }) => (
  <button
    type="button"
    onClick={() => onClick(value.v)}
    className={[
      'text-xs font-semibold rounded-full px-3 py-1.5 border-2 transition-all',
      current === value.v
        ? 'bg-rose-600 border-rose-600 text-white shadow-sm'
        : 'bg-white border-gray-200 text-gray-700 hover:border-rose-300',
    ].join(' ')}
    data-testid={testid}
    aria-pressed={current === value.v}
  >
    {value.emoji && <span className="mr-1" aria-hidden="true">{value.emoji}</span>}
    {value.label}
  </button>
);

const MaternalPostpartum = () => {
  const navigate = useNavigate();
  const [snap, setSnap] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  const [bleeding, setBleeding] = useState('light');
  const [mood, setMood] = useState('okay');
  const [feeding, setFeeding] = useState('breastfeeding');
  const [pain, setPain] = useState('mild');
  const [sleepHours, setSleepHours] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    Promise.all([
      api.snapshot().catch(() => null),
      api.pPRecent().catch(() => ({ data: [] })),
    ])
      .then(([s, r]) => { setSnap(s); setRecent(r?.data || []); })
      .finally(() => setLoading(false));
  }, []);

  const stage = snap?.stage_info?.stage;
  const eligible = ['postpartum', 'recovery'].includes(stage);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!eligible) {
      toast.error('Postpartum tracking is for the postpartum or recovery stage.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        bleeding, mood, feeding, pain,
        sleep_hours: sleepHours === '' ? null : Math.max(0, Math.min(24, parseInt(sleepHours, 10) || 0)),
        notes: notes.trim() || null,
      };
      const res = await api.postPP(payload);
      setResult(res);
      const r = await api.pPRecent().catch(() => ({ data: [] }));
      setRecent(r?.data || []);
      if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <MaternalLoader label="Loading postpartum support…" testid="maternal-postpartum-loading" />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/40 via-white to-white pb-24" data-testid="maternal-postpartum">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <MaternalBackLink />

        <header className="mb-5">
          <div className="flex items-center gap-3 mb-1">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center shrink-0">
              <Baby className="h-5 w-5 text-white" />
            </span>
            <div>
              <p className="text-[11px] font-semibold text-purple-700 uppercase tracking-widest">Postpartum support</p>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">Recovery, day by day</h1>
            </div>
          </div>
        </header>

        {/* Result banner */}
        {result && (
          <article className="rounded-2xl bg-white border-2 border-purple-100 shadow-sm p-5 mb-4" data-testid="maternal-postpartum-result">
            <p className="text-[11px] font-semibold text-purple-700 uppercase tracking-widest mb-1">Saved</p>
            <p className="text-sm text-gray-800 leading-relaxed italic">{result.reassurance}</p>
            {(result.warnings || []).length > 0 && (
              <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3">
                <p className="text-[10px] font-bold text-amber-900 uppercase tracking-wider mb-1.5">When to reach out</p>
                <ul className="space-y-1">
                  {result.warnings.map((w) => (
                    <li key={w} className="text-xs text-amber-900 flex items-start gap-1.5 leading-relaxed">
                      <span className="w-1 h-1 rounded-full bg-amber-600 mt-1.5 shrink-0" aria-hidden="true" />
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </article>
        )}

        {/* Eligibility gate */}
        {!eligible && (
          <article className="rounded-2xl border-2 border-dashed border-gray-200 bg-white p-5 mb-4 text-center" data-testid="maternal-postpartum-locked">
            <Baby className="h-7 w-7 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-700 font-semibold">Postpartum tracking unlocks after birth</p>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
              Once you mark your postpartum start date in your stage settings, you'll be able to log bleeding, mood, feeding, pain, and sleep here.
            </p>
            <button
              onClick={() => navigate('/maternal-health')}
              className="mt-3 inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-xl px-4 py-2 transition-all"
            >
              Go to stage setup
            </button>
          </article>
        )}

        {/* Form */}
        {eligible && (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="maternal-postpartum-form">
            <div data-testid="maternal-postpartum-bleeding">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Bleeding</p>
              <div className="flex flex-wrap gap-1.5">
                {BLEEDING.map((o) => (
                  <Pill key={o.v} value={o} current={bleeding} onClick={setBleeding} testid={`maternal-postpartum-bleeding-${o.v}`} />
                ))}
              </div>
            </div>

            <div data-testid="maternal-postpartum-mood">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Mood</p>
              <div className="flex flex-wrap gap-1.5">
                {MOOD.map((o) => (
                  <Pill key={o.v} value={o} current={mood} onClick={setMood} testid={`maternal-postpartum-mood-${o.v}`} />
                ))}
              </div>
            </div>

            <div data-testid="maternal-postpartum-feeding">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Feeding</p>
              <div className="flex flex-wrap gap-1.5">
                {FEEDING.map((o) => (
                  <Pill key={o.v} value={o} current={feeding} onClick={setFeeding} testid={`maternal-postpartum-feeding-${o.v}`} />
                ))}
              </div>
            </div>

            <div data-testid="maternal-postpartum-pain">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Pain</p>
              <div className="flex flex-wrap gap-1.5">
                {PAIN.map((o) => (
                  <Pill key={o.v} value={o} current={pain} onClick={setPain} testid={`maternal-postpartum-pain-${o.v}`} />
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="pp-sleep" className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2 block">
                Sleep last night (hours, optional)
              </label>
              <input
                id="pp-sleep"
                type="number"
                inputMode="numeric"
                min={0}
                max={24}
                value={sleepHours}
                onChange={(e) => setSleepHours(e.target.value)}
                placeholder="e.g. 5"
                className="w-32 rounded-xl border-2 border-gray-100 bg-white p-2.5 text-sm focus:border-rose-300 focus:outline-none transition-colors"
                data-testid="maternal-postpartum-sleep"
              />
            </div>

            <div>
              <label htmlFor="pp-notes" className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2 block">
                Notes (optional)
              </label>
              <textarea
                id="pp-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Anything you want to remember about today…"
                className="w-full rounded-xl border-2 border-gray-100 bg-white p-3 text-sm focus:border-rose-300 focus:outline-none transition-colors"
                data-testid="maternal-postpartum-notes"
              />
              <p className="text-[10px] text-gray-400 text-right mt-1">{notes.length}/500</p>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-gradient-to-br from-purple-500 to-fuchsia-500 hover:brightness-110 disabled:opacity-60 text-white font-bold rounded-xl px-5 py-4 shadow-sm transition-all"
              data-testid="maternal-postpartum-submit"
            >
              {submitting && <Loader2 className="h-5 w-5 inline-block animate-spin mr-2" />}
              {submitting ? 'Saving…' : 'Save today'}
            </button>
          </form>
        )}

        {/* Recent log */}
        <section className="mt-6" data-testid="maternal-postpartum-recent">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2 px-1">Recent days</p>
          {recent.length === 0 ? (
            <p className="text-xs text-gray-500 italic px-1">No postpartum entries yet.</p>
          ) : (
            <ul className="space-y-2">
              {recent.slice(0, 7).map((row) => (
                <li
                  key={row.id}
                  className="rounded-2xl bg-white border border-gray-100 shadow-sm p-3"
                  data-testid={`maternal-postpartum-row-${row.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <p className="text-[11px] font-semibold text-gray-500">{relTime(row.created_at)}</p>
                    {row.sleep_hours != null && (
                      <span className="text-[11px] text-gray-600 inline-flex items-center gap-1">
                        <Moon className="h-3 w-3" />
                        {row.sleep_hours}h
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-[11px]">
                    <span className="inline-flex items-center gap-1 bg-rose-50 text-rose-800 border border-rose-200 rounded-md px-1.5 py-0.5">
                      <Droplet className="h-3 w-3" />
                      {row.bleeding}
                    </span>
                    <span className="inline-flex items-center gap-1 bg-purple-50 text-purple-800 border border-purple-200 rounded-md px-1.5 py-0.5">
                      <Heart className="h-3 w-3" />
                      {row.mood}
                    </span>
                    <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-900 border border-amber-200 rounded-md px-1.5 py-0.5">
                      <Activity className="h-3 w-3" />
                      pain · {row.pain}
                    </span>
                    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-md px-1.5 py-0.5">
                      {row.feeding}
                    </span>
                  </div>
                  {row.notes && <p className="text-xs text-gray-700 leading-relaxed mt-2">{row.notes}</p>}
                </li>
              ))}
            </ul>
          )}
        </section>

        <MaternalDisclaimer />
      </div>
    </div>
  );
};

export default MaternalPostpartum;
